import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { RefreshCw, Upload, CheckCircle2, AlertTriangle, ImageOff } from "lucide-react";

const BK = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "");

export default function ImageResyncTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const inputs = useRef({});

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/media/health"); setData(data); }
    catch { toast.error("Could not load image health"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const replace = async (id, file) => {
    if (!file) return;
    setBusyId(id);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/media/${id}/replace`, fd);
      toast.success("Image restored");
      setData((d) => ({
        ...d,
        files: d.files.map((f) => (f.id === id ? { ...f, available: true, _restored: true, _bust: Date.now() } : f)),
        missing: Math.max(0, (d.missing || 0) - 1),
      }));
    } catch (e) { toast.error(e.response?.data?.detail || "Replace failed"); }
    finally { setBusyId(null); }
  };

  if (loading && !data) return <div className="mt-4 text-sm text-cass-slate" data-testid="resync-loading">Checking your images…</div>;

  const files = data?.files || [];
  const missing = files.filter((f) => !f.available);
  const shown = showAll ? files : missing;

  return (
    <div className="mt-4 space-y-4 max-w-3xl" data-testid="image-resync-tab">
      <Card className="p-5 rounded-md space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-alt font-semibold text-cass-navy">Restore missing images</h3>
            <p className="text-sm text-cass-slate mt-1 max-w-xl">
              After a deploy, image files can go missing even though their records remain — you'll see broken image icons in proposals and PDFs.
              Re-upload each file below and it's slotted back into <b>exactly the same place</b>, so every proposal, testimonial and template that used it works again. No settings to change.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="resync-refresh"><RefreshCw className={"w-4 h-4 mr-1" + (loading ? " animate-spin" : "")} />Re-check</Button>
        </div>
        <div className="flex items-center gap-3 text-sm">
          {data?.missing > 0 ? (
            <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid="resync-missing-count"><AlertTriangle className="w-3.5 h-3.5 mr-1" />{data.missing} missing</Badge>
          ) : (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="resync-all-ok"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />All {data?.total || 0} images present</Badge>
          )}
          <span className="text-cass-slate">{data?.total || 0} total</span>
          <button className="ml-auto text-cass-teal hover:underline" onClick={() => setShowAll((v) => !v)} data-testid="resync-toggle-all">
            {showAll ? "Show only missing" : "Show all images"}
          </button>
        </div>
      </Card>

      {shown.length === 0 && (
        <div className="text-sm text-cass-slate py-8 text-center border border-dashed border-border rounded-md" data-testid="resync-empty">
          {showAll ? "No images found." : "Nothing missing — every image is loading correctly. 🎉"}
        </div>
      )}

      {shown.map((f) => (
        <Card key={f.id} className="p-4 rounded-md flex items-center gap-4" data-testid={`resync-row-${f.id}`}>
          <div className="w-16 h-16 rounded-md border border-border bg-slate-50 grid place-items-center overflow-hidden shrink-0">
            {f.available
              ? <img src={`${BK}${f.url}${f._bust ? `?t=${f._bust}` : ""}`} alt="" className="w-full h-full object-contain" data-testid={`resync-thumb-${f.id}`} />
              : <ImageOff className="w-6 h-6 text-slate-300" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-cass-navy truncate">{f.filename || "(unnamed)"}</div>
            <div className="text-xs text-cass-slate mt-0.5">Uploaded {fmtDate(f.created_at)}</div>
            <div className="mt-1">
              {f.available
                ? <span className="inline-flex items-center gap-1 text-xs text-emerald-600" data-testid={`resync-status-ok-${f.id}`}><CheckCircle2 className="w-3.5 h-3.5" />{f._restored ? "Restored" : "Present"}</span>
                : <span className="inline-flex items-center gap-1 text-xs text-amber-700" data-testid={`resync-status-missing-${f.id}`}><AlertTriangle className="w-3.5 h-3.5" />Missing — needs re-upload</span>}
            </div>
          </div>
          <div className="shrink-0">
            <input ref={(el) => (inputs.current[f.id] = el)} type="file" accept="image/*" className="hidden"
              onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; replace(f.id, file); }}
              data-testid={`resync-input-${f.id}`} />
            <Button size="sm" variant={f.available ? "outline" : "default"} className={f.available ? "" : "bg-cass-teal text-white"}
              disabled={busyId === f.id} onClick={() => inputs.current[f.id]?.click()} data-testid={`resync-upload-${f.id}`}>
              <Upload className="w-4 h-4 mr-1" />{busyId === f.id ? "Uploading…" : f.available ? "Replace" : "Re-upload"}
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
}
